import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Gift,
  Users,
  Code2,
  Activity,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Search,
  X,
  Plus,
  Edit3,
  Trash2,
  TrendingUp,
  CreditCard,
  Percent,
  Calendar,
  BarChart3,
  ArrowRight,
  UserCheck,
  Tag,
} from 'lucide-react';
import { useReferralOverview, useReferralUsers, useInfluencerCodes, usePromoCodes, useReferralActivity } from '../hooks/useReferrals';
import { referralsService } from '../services/referrals';
import { useUserList } from '../hooks/useUsers';
import type {
  ReferralUserDetail,
  InfluencerCodeDetail,
  CreateInfluencerCodeRequest,
  UpdateInfluencerCodeRequest,
  ReferralStatus,
  InfluencerStatus,
  ActivityType,
  PayoutMethod,
  ActivityItem,
  InfluencerCodeItem,
  ReferralUserItem,
  PromoCodeItem,
  CreatePromoCodeResponse,
  PromoCodeRedemptionsResponse,
  CreatePromoCodeRequest,
} from '../types/referrals';
import { cn, formatDateTime, formatRelativeTime, formatNumber, formatCentsDecimal, formatPercent, showToast } from '../utils/helpers';
import { Badge } from '../components/ui';

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 20;
const USER_PICKER_PAGE_SIZE = 10;

type TabKey = 'overview' | 'users' | 'referees' | 'influencer-codes' | 'promo-codes' | 'activity';

// ============================================================================
// Badge helpers
// ============================================================================

function referralStatusBadge(status: string): 'success' | 'warning' | 'error' | 'info' | 'muted' {
  const map: Record<string, 'success' | 'warning' | 'error' | 'info' | 'muted'> = {
    PENDING: 'warning',
    QUALIFIED: 'info',
    CREDITS_ISSUED: 'success',
    INVALID: 'error',
  };
  return map[status] || 'muted';
}

function influencerStatusBadge(status: string): 'success' | 'warning' | 'error' | 'info' | 'muted' {
  const map: Record<string, 'success' | 'warning' | 'error' | 'info' | 'muted'> = {
    PENDING: 'warning',
    APPROVED: 'success',
    SUSPENDED: 'error',
    TERMINATED: 'muted',
  };
  return map[status] || 'muted';
}

function activityTypeBadge(type: string): 'success' | 'warning' | 'error' | 'info' | 'muted' {
  const map: Record<string, 'success' | 'warning' | 'error' | 'info' | 'muted'> = {
    SIGNUP: 'info',
    QUALIFIED: 'warning',
    CREDITS_ISSUED: 'success',
    CREDIT_USED: 'muted',
    INVALIDATED: 'error',
  };
  return map[type] || 'muted';
}

// ============================================================================
// Shared sub-components
// ============================================================================

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon: Icon, color, bgColor, loading }) => (
  <div className="card p-4">
    <div className="flex items-center gap-3">
      <div className={cn('p-2 rounded-xl flex-shrink-0', bgColor)}>
        <Icon className={cn('h-5 w-5', color)} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground truncate">{label}</p>
        {loading ? (
          <div className="h-7 w-16 bg-muted/30 rounded animate-pulse mt-0.5" />
        ) : (
          <p className="text-xl font-bold text-foreground">{value}</p>
        )}
      </div>
    </div>
  </div>
);

const Pagination: React.FC<{
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
}> = ({ page, totalPages, total, onPageChange, loading }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
      <p className="text-xs text-muted-foreground">Page {page} of {totalPages} ({formatNumber(total)} total)</p>
      <div className="flex items-center gap-1">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1 || loading} className="p-1.5 rounded-lg hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          <ChevronLeft className="h-4 w-4 text-foreground" />
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum: number;
          if (totalPages <= 5) pageNum = i + 1;
          else if (page <= 3) pageNum = i + 1;
          else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
          else pageNum = page - 2 + i;
          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              disabled={loading}
              className={cn('w-8 h-8 text-xs rounded-lg transition-colors', pageNum === page ? 'bg-primary/20 text-primary font-semibold' : 'text-muted-foreground hover:bg-primary/10')}
            >
              {pageNum}
            </button>
          );
        })}
        <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages || loading} className="p-1.5 rounded-lg hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          <ChevronRight className="h-4 w-4 text-foreground" />
        </button>
      </div>
    </div>
  );
};

const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="pt-2 mt-2 first:mt-0 first:pt-0">
    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{title}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">{children}</div>
  </div>
);

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="text-[10px] text-muted-foreground whitespace-nowrap">{label}</span>
      <span className="text-[10px] text-foreground text-right break-all">{value}</span>
    </div>
  );
};

// ============================================================================
// Confirm Modal
// ============================================================================

const ConfirmModal: React.FC<{
  open: boolean;
  title: string;
  children: React.ReactNode;
  confirmLabel: string;
  confirmVariant?: 'primary' | 'danger';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ open, title, children, confirmLabel, confirmVariant = 'primary', loading, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-card border border-border rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <button onClick={onCancel} className="p-1 rounded-lg hover:bg-primary/10 transition-colors">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
        <div className="text-xs text-muted-foreground space-y-2 mb-6">{children}</div>
        <div className="flex items-center justify-end gap-2">
          <button onClick={onCancel} disabled={loading} className="px-4 py-2 text-xs font-medium text-muted-foreground rounded-lg hover:bg-primary/10 transition-colors">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={cn(
              'px-4 py-2 text-xs font-medium rounded-lg transition-colors flex items-center gap-2',
              confirmVariant === 'danger'
                ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                : 'bg-primary/20 text-primary hover:bg-primary/30'
            )}
          >
            {loading && <Loader2 className="h-3 w-3 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Form Modal (for Create/Edit Influencer Code)
// ============================================================================

interface FormModalProps {
  open: boolean;
  title: string;
  loading?: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  submitLabel: string;
  children: React.ReactNode;
}

const FormModal: React.FC<FormModalProps> = ({ open, title, loading, onSubmit, onCancel, submitLabel, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-card border border-border rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <button onClick={onCancel} className="p-1 rounded-lg hover:bg-primary/10 transition-colors">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
        <div className="space-y-4 mb-6">{children}</div>
        <div className="flex items-center justify-end gap-2">
          <button onClick={onCancel} disabled={loading} className="px-4 py-2 text-xs font-medium text-muted-foreground rounded-lg hover:bg-primary/10 transition-colors">
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={loading}
            className="px-4 py-2 text-xs font-medium rounded-lg bg-primary/20 text-primary hover:bg-primary/30 transition-colors flex items-center gap-2"
          >
            {loading && <Loader2 className="h-3 w-3 animate-spin" />}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// User Picker
//
// One component, two shapes: a single pick (influencer codes) and a multi
// pick (a promo code aimed at several named users). Picked users show as
// removable chips; the search list stays open in multi mode so several can be
// added in a row.
// ============================================================================

export interface PickedUser {
  id: number;
  label: string;
}

const UserPicker: React.FC<{
  selected: PickedUser[];
  multiple?: boolean;
  onAdd: (user: PickedUser) => void;
  onRemove: (userId: number) => void;
}> = ({ selected, multiple = false, onAdd, onRemove }) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pickerPage, setPickerPage] = useState(1);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPickerPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const userListParams = useMemo(() => ({
    search: debouncedSearch || undefined,
    limit: USER_PICKER_PAGE_SIZE,
    offset: (pickerPage - 1) * USER_PICKER_PAGE_SIZE,
  }), [debouncedSearch, pickerPage]);

  const { items: users, loading: usersLoading, totalPages: usersTotalPages } = useUserList(userListParams);

  const selectedIds = useMemo(() => new Set(selected.map((u) => u.id)), [selected]);

  const chips = selected.length > 0 && (
    <div className={cn('flex flex-col gap-1.5', multiple && 'mb-2')}>
      {selected.map((user) => (
        <div
          key={user.id}
          className="flex items-center gap-2 p-2 bg-primary/10 border border-primary/30 rounded-lg"
        >
          <span className="text-xs text-foreground flex-1">{user.label}</span>
          <button onClick={() => onRemove(user.id)} className="p-1 rounded hover:bg-primary/20 transition-colors">
            <X className="h-3 w-3 text-muted-foreground" />
          </button>
        </div>
      ))}
    </div>
  );

  // A single pick is done once something is chosen; a multi pick keeps
  // searching so the next user is one click away.
  if (!multiple && selected.length > 0) {
    return <div>{chips}</div>;
  }

  return (
    <div>
      {chips}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          placeholder={multiple ? 'Search to add another user...' : 'Search users by name, email...'}
          className="w-full pl-8 pr-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
        />
      </div>
      {isOpen && (
        <div className="mt-1 border border-border rounded-lg bg-card max-h-48 overflow-y-auto">
          {usersLoading && users.length === 0 ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 text-primary animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground text-xs">No users found</div>
          ) : (
            <div className="divide-y divide-border">
              {users.map((user) => (
                <button
                  key={user.id}
                  disabled={selectedIds.has(user.id)}
                  onClick={() => {
                    onAdd({ id: user.id, label: `${user.name || user.email || 'User'} #${user.id}` });
                    // Multi mode stays open — the admin is usually adding a
                    // handful of people in one go.
                    if (!multiple) setIsOpen(false);
                    setSearch('');
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-primary/5 transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  <div className="w-6 h-6 rounded-full bg-muted/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {user.profileImage ? (
                      <img src={user.profileImage} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-[10px] font-semibold text-muted-foreground">
                        {(user.name || user.email || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground truncate">
                      {user.name || 'No name'} <span className="text-muted-foreground">#{user.id}</span>
                      {selectedIds.has(user.id) && <span className="text-muted-foreground"> · added</span>}
                    </p>
                    <p className="text-[10px] text-muted-foreground truncate">{user.email || user.phone || 'No contact'}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
          {usersTotalPages > 1 && (
            <div className="px-3 py-1.5 border-t border-border flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground">Page {pickerPage}/{usersTotalPages}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPickerPage((p) => Math.max(1, p - 1))} disabled={pickerPage <= 1} className="p-0.5 rounded hover:bg-primary/10 disabled:opacity-30 transition-colors">
                  <ChevronLeft className="h-3 w-3 text-foreground" />
                </button>
                <button onClick={() => setPickerPage((p) => Math.min(usersTotalPages, p + 1))} disabled={pickerPage >= usersTotalPages} className="p-0.5 rounded hover:bg-primary/10 disabled:opacity-30 transition-colors">
                  <ChevronRight className="h-3 w-3 text-foreground" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Single-pick wrapper — the shape the influencer-code form uses. */
const SingleUserPicker: React.FC<{
  selectedUserId: number | null;
  selectedUserLabel: string;
  onSelect: (userId: number, label: string) => void;
  onClear: () => void;
}> = ({ selectedUserId, selectedUserLabel, onSelect, onClear }) => (
  <UserPicker
    selected={selectedUserId ? [{ id: selectedUserId, label: selectedUserLabel }] : []}
    onAdd={(user) => onSelect(user.id, user.label)}
    onRemove={onClear}
  />
);

// ============================================================================
// Tab 1: Overview
// ============================================================================

const OverviewTab: React.FC = () => {
  const { data, loading } = useReferralOverview();

  return (
    <div className="space-y-4">
      {/* Primary stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total Referrals" value={data ? formatNumber(data.totalReferrals) : '—'} icon={Users} color="text-blue-400" bgColor="bg-blue-400/10" loading={loading} />
        <StatCard label="Completion Rate" value={data ? formatPercent(data.completionRate) : '—'} icon={Percent} color="text-emerald-400" bgColor="bg-emerald-400/10" loading={loading} />
        <StatCard label="Credits Issued" value={data ? formatCentsDecimal(data.credits.totalIssuedCents) : '—'} icon={CreditCard} color="text-purple-400" bgColor="bg-purple-400/10" loading={loading} />
        <StatCard label="Credits Remaining" value={data ? formatCentsDecimal(data.credits.totalRemainingCents) : '—'} icon={Gift} color="text-yellow-400" bgColor="bg-yellow-400/10" loading={loading} />
      </div>

      {/* Recent activity stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Users with Code" value={data ? formatNumber(data.totalUsersWithCode) : '—'} icon={Users} color="text-orange-400" bgColor="bg-orange-400/10" loading={loading} />
        <StatCard label="Last 7 Days" value={data ? formatNumber(data.recentActivity.last7Days) : '—'} icon={Calendar} color="text-cyan-400" bgColor="bg-cyan-400/10" loading={loading} />
        <StatCard label="Last 30 Days" value={data ? formatNumber(data.recentActivity.last30Days) : '—'} icon={BarChart3} color="text-indigo-400" bgColor="bg-indigo-400/10" loading={loading} />
        <StatCard label="Growth Rate" value={data ? formatPercent(data.recentActivity.growthRate) : '—'} icon={TrendingUp} color="text-emerald-400" bgColor="bg-emerald-400/10" loading={loading} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Status breakdown */}
        <div className="card p-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Status Breakdown</h3>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => <div key={i} className="h-6 bg-muted/20 rounded animate-pulse" />)}
            </div>
          ) : data ? (
            <div className="space-y-2">
              {([
                ['Pending', data.statusBreakdown.pending, 'bg-yellow-400'],
                ['Qualified', data.statusBreakdown.qualified, 'bg-blue-400'],
                ['Credits Issued', data.statusBreakdown.creditsIssued, 'bg-emerald-400'],
                ['Invalid', data.statusBreakdown.invalid, 'bg-red-400'],
              ] as const).map(([label, count, barColor]) => {
                const total = data.totalReferrals || 1;
                const pct = (count / total) * 100;
                return (
                  <div key={label}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs text-foreground">{label}</span>
                      <span className="text-xs text-muted-foreground">{formatNumber(count)} ({pct.toFixed(1)}%)</span>
                    </div>
                    <div className="h-2 bg-muted/20 rounded-full overflow-hidden">
                      <div className={cn('h-full rounded-full transition-all', barColor)} style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        {/* Credits summary */}
        <div className="card p-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Credits Summary</h3>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => <div key={i} className="h-8 bg-muted/20 rounded animate-pulse" />)}
            </div>
          ) : data ? (
            <div className="grid grid-cols-2 gap-3">
              {([
                ['Issued', data.credits.totalIssuedCents, 'text-blue-400'],
                ['Used', data.credits.totalUsedCents, 'text-emerald-400'],
                ['Remaining', data.credits.totalRemainingCents, 'text-yellow-400'],
                ['Available', data.credits.totalAvailableCents, 'text-purple-400'],
              ] as const).map(([label, cents, textColor]) => (
                <div key={label} className="text-center p-3 bg-muted/10 rounded-lg">
                  <p className={cn('text-sm font-bold', textColor)}>{formatCentsDecimal(cents)}</p>
                  <p className="text-[10px] text-muted-foreground">{label}</p>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {/* Top Referrers */}
      <div className="card p-4">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Top Referrers</h3>
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <div key={i} className="h-10 bg-muted/20 rounded animate-pulse" />)}
          </div>
        ) : data && data.topReferrers.length > 0 ? (
          <div className="divide-y divide-border">
            {data.topReferrers.slice(0, 10).map((ref, idx) => (
              <div key={ref.userId} className="flex items-center gap-3 py-2">
                <span className="text-xs text-muted-foreground w-5 text-right">{idx + 1}.</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground truncate">
                    {ref.name || ref.email || `User #${ref.userId}`}
                    {ref.referralCode && (
                      <span className="text-[10px] font-mono text-muted-foreground ml-1.5">{ref.referralCode}</span>
                    )}
                  </p>
                </div>
                <span className="text-xs text-foreground font-medium">{formatNumber(ref.referralCount)} referrals</span>
                {ref.isInfluencer && <Badge variant="info" size="sm">Influencer</Badge>}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground text-center py-4">No referrer data available</p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Tab 2: Users
// ============================================================================

const UsersTab: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [hasReferrals, setHasReferrals] = useState<boolean | undefined>(undefined);
  const [isInfluencer, setIsInfluencer] = useState<boolean | undefined>(undefined);
  const [sortBy, setSortBy] = useState<'referralCount' | 'completedCount' | 'earningsCents' | 'createdAt'>('referralCount');
  const [page, setPage] = useState(1);
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [userDetail, setUserDetail] = useState<{ userId: number; data: ReferralUserDetail | null; loading: boolean } | null>(null);
  const [userReferees, setUserReferees] = useState<{ userId: number; items: ActivityItem[]; total: number; loading: boolean } | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const params = useMemo(() => ({
    search: debouncedSearch || undefined,
    hasReferrals,
    isInfluencer,
    sortBy,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [debouncedSearch, hasReferrals, isInfluencer, sortBy, page]);

  const { users, total, totalPages, loading, page: currentPage } = useReferralUsers(params);

  const handleExpand = useCallback(async (userId: number, referralCode: string | null) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      setUserDetail(null);
      setUserReferees(null);
      return;
    }
    setExpandedUserId(userId);
    setUserDetail({ userId, data: null, loading: true });

    // Fetch user detail
    try {
      const data = await referralsService.getUserDetail(userId);
      setUserDetail({ userId, data, loading: false });
    } catch {
      setUserDetail({ userId, data: null, loading: false });
    }

    // Fetch referees via activity endpoint filtered by this user's referral code
    if (referralCode) {
      setUserReferees({ userId, items: [], total: 0, loading: true });
      try {
        const activityData = await referralsService.getActivity({ referralCode, limit: 100 });
        const items = Array.isArray(activityData.activities) ? activityData.activities : [];
        setUserReferees({ userId, items, total: activityData.total ?? items.length, loading: false });
      } catch {
        setUserReferees({ userId, items: [], total: 0, loading: false });
      }
    } else {
      setUserReferees(null);
    }
  }, [expandedUserId]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users..."
            className="w-full pl-8 pr-8 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
              <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>

        <button
          onClick={() => setHasReferrals(hasReferrals === true ? undefined : true)}
          className={cn(
            'px-3 py-2 text-xs rounded-lg border transition-all',
            hasReferrals === true
              ? 'bg-primary/15 text-primary border-primary/30 font-medium'
              : 'bg-card text-muted-foreground border-border hover:bg-primary/10'
          )}
        >
          Has Referrals
        </button>

        <button
          onClick={() => setIsInfluencer(isInfluencer === true ? undefined : true)}
          className={cn(
            'px-3 py-2 text-xs rounded-lg border transition-all',
            isInfluencer === true
              ? 'bg-primary/15 text-primary border-primary/30 font-medium'
              : 'bg-card text-muted-foreground border-border hover:bg-primary/10'
          )}
        >
          Influencer
        </button>

        <select
          value={sortBy}
          onChange={(e) => { setSortBy(e.target.value as typeof sortBy); setPage(1); }}
          className="px-3 py-2 text-xs bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
        >
          <option value="referralCount">Sort: Referrals</option>
          <option value="completedCount">Sort: Completed</option>
          <option value="earningsCents">Sort: Earnings</option>
          <option value="createdAt">Sort: Date</option>
        </select>
      </div>

      {/* User list */}
      {loading && users.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : users.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No users found</div>
      ) : (
        <div className="space-y-2">
          {users.map((user) => (
            <UserRow
              key={user.userId}
              user={user}
              expanded={expandedUserId === user.userId}
              detail={userDetail?.userId === user.userId ? userDetail : null}
              referees={userReferees?.userId === user.userId ? userReferees : null}
              onToggle={() => handleExpand(user.userId, user.referralCode)}
            />
          ))}
        </div>
      )}

      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

// ============================================================================
// Referrals Made Sub-List (with client-side pagination)
// ============================================================================

const REFERRALS_PAGE_SIZE = 5;

const ReferralsMadeList: React.FC<{
  referrals: ReferralUserDetail['referralsMade'];
}> = ({ referrals }) => {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(referrals.length / REFERRALS_PAGE_SIZE));
  const start = (page - 1) * REFERRALS_PAGE_SIZE;
  const visible = referrals.slice(start, start + REFERRALS_PAGE_SIZE);

  return (
    <div className="pt-2 mt-2">
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
        Users Who Used This Code ({referrals.length})
      </p>
      <div className="space-y-1">
        {visible.map((ref) => (
          <div key={ref.id} className="flex items-center gap-2 p-2 bg-muted/10 rounded-lg text-[10px]">
            <span className="text-foreground truncate flex-1">
              {ref.refereeName || ref.refereeEmail || `User #${ref.refereeUserId}`}
            </span>
            <Badge variant={referralStatusBadge(ref.status)} size="sm">{ref.status}</Badge>
            {ref.referrerRewardCents != null && ref.referrerRewardCents > 0 && (
              <span className="text-emerald-400">{formatCentsDecimal(ref.referrerRewardCents)}</span>
            )}
            {ref.refereeRewardCents != null && ref.refereeRewardCents > 0 && (
              <span className="text-blue-400">+{formatCentsDecimal(ref.refereeRewardCents)} referee</span>
            )}
            <span className="text-muted-foreground">{formatRelativeTime(ref.createdAt)}</span>
          </div>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/30">
          <p className="text-[10px] text-muted-foreground">
            Page {page} of {totalPages} ({referrals.length} users)
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-1 rounded hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-3 w-3 text-foreground" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-1 rounded hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="h-3 w-3 text-foreground" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Referees from Activity Feed (with client-side pagination)
// ============================================================================

const REFEREES_PAGE_SIZE = 5;

const RefereesFromActivity: React.FC<{
  referees: { userId: number; items: ActivityItem[]; total: number; loading: boolean } | null;
  referralCode: string | null;
}> = ({ referees, referralCode }) => {
  const [page, setPage] = useState(1);

  if (!referralCode) return null;

  if (!referees || referees.loading) {
    return (
      <div className="pt-2 mt-2">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
          People Who Used Code: <span className="font-mono text-foreground">{referralCode}</span>
        </p>
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  if (referees.items.length === 0) {
    return (
      <div className="pt-2 mt-2">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
          People Who Used Code: <span className="font-mono text-foreground">{referralCode}</span>
        </p>
        <p className="text-[10px] text-muted-foreground text-center py-3">No one has used this code yet.</p>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(referees.items.length / REFEREES_PAGE_SIZE));
  const start = (page - 1) * REFEREES_PAGE_SIZE;
  const visible = referees.items.slice(start, start + REFEREES_PAGE_SIZE);

  return (
    <div className="pt-2 mt-2">
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
        People Who Used Code: <span className="font-mono text-foreground">{referralCode}</span> ({referees.items.length})
      </p>
      <div className="space-y-1">
        {visible.map((item) => (
          <div key={item.id} className="flex items-center gap-2 p-2 bg-muted/10 rounded-lg text-[10px]">
            <span className="text-foreground truncate flex-1">
              {item.refereeName || item.refereeEmail || `User #${item.refereeUserId}`}
            </span>
            <Badge variant={activityTypeBadge(item.type)} size="sm">{item.type}</Badge>
            <Badge variant={referralStatusBadge(item.status)} size="sm">{item.status}</Badge>
            {item.referrerRewardCents != null && item.referrerRewardCents > 0 && (
              <span className="text-emerald-400">{formatCentsDecimal(item.referrerRewardCents)}</span>
            )}
            {item.refereeRewardCents != null && item.refereeRewardCents > 0 && (
              <span className="text-blue-400">+{formatCentsDecimal(item.refereeRewardCents)}</span>
            )}
            <span className="text-muted-foreground">{formatRelativeTime(item.timestamp || item.createdAt)}</span>
          </div>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/30">
          <p className="text-[10px] text-muted-foreground">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-1 rounded hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-3 w-3 text-foreground" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-1 rounded hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="h-3 w-3 text-foreground" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const UserRow: React.FC<{
  user: ReferralUserItem;
  expanded: boolean;
  detail: { userId: number; data: ReferralUserDetail | null; loading: boolean } | null;
  referees: { userId: number; items: ActivityItem[]; total: number; loading: boolean } | null;
  onToggle: () => void;
}> = ({ user, expanded, detail, referees, onToggle }) => (
  <div className={cn('card p-3 cursor-pointer transition-all', expanded && 'ring-1 ring-primary/30')} onClick={onToggle}>
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex-shrink-0">
          {expanded ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </div>
        <span className="text-xs text-foreground truncate max-w-[180px]">{user.name || user.email || `User #${user.userId}`}</span>
        {user.email && user.name && <span className="text-[10px] text-muted-foreground truncate max-w-[150px] hidden sm:inline">{user.email}</span>}
        {user.referralCode && <span className="text-[10px] font-mono text-muted-foreground">{user.referralCode}</span>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] text-muted-foreground">{formatNumber(user.referralCount)} referrals</span>
        <span className="text-[10px] text-muted-foreground">{formatNumber(user.completedCount)} completed</span>
        {user.earningsCents > 0 && <Badge variant="success" size="sm">{formatCentsDecimal(user.earningsCents)}</Badge>}
        {user.influencerProfile && <Badge variant="info" size="sm">Influencer</Badge>}
      </div>
    </div>

    {expanded && (
      <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
        {/* User detail section */}
        {detail?.loading ? (
          <div className="flex items-center justify-center py-4"><Loader2 className="h-5 w-5 text-primary animate-spin" /></div>
        ) : detail?.data ? (
          <>
            {detail.data.statusBreakdown && (
              <DetailSection title="Referral Stats">
                <DetailRow label="Pending" value={formatNumber(detail.data.statusBreakdown.pending)} />
                <DetailRow label="Qualified" value={formatNumber(detail.data.statusBreakdown.qualified)} />
                <DetailRow label="Credits Issued" value={formatNumber(detail.data.statusBreakdown.creditsIssued)} />
                <DetailRow label="Invalid" value={formatNumber(detail.data.statusBreakdown.invalid)} />
              </DetailSection>
            )}

            {detail.data.influencerProfile && (
              <DetailSection title="Influencer Profile">
                <DetailRow label="Code" value={detail.data.influencerProfile.code} />
                <DetailRow label="Status" value={<Badge variant={influencerStatusBadge(detail.data.influencerProfile.status)} size="sm">{detail.data.influencerProfile.status}</Badge>} />
                <DetailRow label="Total Earnings" value={formatCentsDecimal(detail.data.influencerProfile.totalEarningsCents)} />
                <DetailRow label="Pending Payout" value={formatCentsDecimal(detail.data.influencerProfile.pendingPayoutCents)} />
                <DetailRow label="Payout Method" value={detail.data.influencerProfile.payoutMethod || 'Not set'} />
              </DetailSection>
            )}

            {detail.data.referredBy && (
              <DetailSection title="Referred By">
                <DetailRow label="Referrer" value={detail.data.referredBy.referrerName || `User #${detail.data.referredBy.referrerUserId}`} />
                <DetailRow label="Code" value={detail.data.referredBy.referralCode} />
                <DetailRow label="Status" value={<Badge variant={referralStatusBadge(detail.data.referredBy.status)} size="sm">{detail.data.referredBy.status}</Badge>} />
                <DetailRow label="Date" value={formatDateTime(detail.data.referredBy.createdAt)} />
              </DetailSection>
            )}

            {detail.data.credits && (
              <DetailSection title="Credits">
                <DetailRow label="Issued" value={formatCentsDecimal(detail.data.credits.totalIssuedCents)} />
                <DetailRow label="Used" value={formatCentsDecimal(detail.data.credits.totalUsedCents)} />
                <DetailRow label="Remaining" value={formatCentsDecimal(detail.data.credits.remainingCents)} />
              </DetailSection>
            )}

            {detail.data.referralsMade && detail.data.referralsMade.length > 0 && (
              <ReferralsMadeList referrals={detail.data.referralsMade} />
            )}
          </>
        ) : null}

        {/* Referees list from activity feed (always fetched independently) */}
        <RefereesFromActivity referees={referees} referralCode={user.referralCode} />
      </div>
    )}
  </div>
);

// ============================================================================
// Tab 3: Influencer Codes
// ============================================================================

const InfluencerCodesTab: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<InfluencerStatus | ''>('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [codeDetail, setCodeDetail] = useState<{ id: number; data: InfluencerCodeDetail | null; loading: boolean } | null>(null);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<{ code: string; userId: number | null; userLabel: string; payoutMethod: PayoutMethod | ''; minPayoutCents: string; notes: string }>({
    code: '', userId: null, userLabel: '', payoutMethod: '', minPayoutCents: '', notes: '',
  });
  const [creating, setCreating] = useState(false);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<{ codeId: number; status: InfluencerStatus; payoutMethod: PayoutMethod | ''; minPayoutCents: string; notes: string }>({
    codeId: 0, status: 'APPROVED', payoutMethod: '', minPayoutCents: '', notes: '',
  });
  const [editing, setEditing] = useState(false);

  // Deactivate
  const [deactivateTarget, setDeactivateTarget] = useState<{ id: number; code: string } | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const params = useMemo(() => ({
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [debouncedSearch, statusFilter, page]);

  const { items, total, totalPages, loading, page: currentPage, refetch } = useInfluencerCodes(params);

  const handleExpand = useCallback(async (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
      setCodeDetail(null);
      return;
    }
    setExpandedId(id);
    setCodeDetail({ id, data: null, loading: true });
    try {
      const data = await referralsService.getInfluencerCodeDetail(id);
      setCodeDetail({ id, data, loading: false });
    } catch {
      setCodeDetail({ id, data: null, loading: false });
      showToast('error', 'Failed to load code detail');
    }
  }, [expandedId]);

  const handleCreate = useCallback(async () => {
    if (!createForm.code || !createForm.userId) return;
    setCreating(true);
    try {
      const body: CreateInfluencerCodeRequest = {
        code: createForm.code,
        userId: createForm.userId,
      };
      if (createForm.payoutMethod) body.payoutMethod = createForm.payoutMethod as PayoutMethod;
      if (createForm.minPayoutCents) body.minPayoutCents = parseInt(createForm.minPayoutCents, 10);
      if (createForm.notes) body.notes = createForm.notes;
      await referralsService.createInfluencerCode(body);
      showToast('success', 'Influencer code created');
      setShowCreateModal(false);
      setCreateForm({ code: '', userId: null, userLabel: '', payoutMethod: '', minPayoutCents: '', notes: '' });
      refetch();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to create code');
    } finally {
      setCreating(false);
    }
  }, [createForm, refetch]);

  const handleEdit = useCallback(async () => {
    setEditing(true);
    try {
      const body: UpdateInfluencerCodeRequest = {};
      if (editForm.status) body.status = editForm.status;
      if (editForm.payoutMethod) body.payoutMethod = editForm.payoutMethod as PayoutMethod;
      if (editForm.minPayoutCents) body.minPayoutCents = parseInt(editForm.minPayoutCents, 10);
      if (editForm.notes !== undefined) body.notes = editForm.notes;
      await referralsService.updateInfluencerCode(editForm.codeId, body);
      showToast('success', 'Influencer code updated');
      setShowEditModal(false);
      refetch();
      // Refresh detail if expanded
      if (expandedId === editForm.codeId) {
        setCodeDetail({ id: editForm.codeId, data: null, loading: true });
        try {
          const data = await referralsService.getInfluencerCodeDetail(editForm.codeId);
          setCodeDetail({ id: editForm.codeId, data, loading: false });
        } catch {
          setCodeDetail({ id: editForm.codeId, data: null, loading: false });
        }
      }
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to update code');
    } finally {
      setEditing(false);
    }
  }, [editForm, refetch, expandedId]);

  const handleDeactivate = useCallback(async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    try {
      await referralsService.deactivateInfluencerCode(deactivateTarget.id);
      showToast('success', `Code "${deactivateTarget.code}" deactivated`);
      setDeactivateTarget(null);
      if (expandedId === deactivateTarget.id) {
        setExpandedId(null);
        setCodeDetail(null);
      }
      refetch();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to deactivate code');
    } finally {
      setDeactivating(false);
    }
  }, [deactivateTarget, refetch, expandedId]);

  const openEditModal = useCallback((item: InfluencerCodeItem, detail: InfluencerCodeDetail | null) => {
    setEditForm({
      codeId: item.id,
      status: item.status,
      payoutMethod: detail?.payoutMethod || '',
      minPayoutCents: detail?.minPayoutCents != null ? String(detail.minPayoutCents) : '',
      notes: detail?.notes || '',
    });
    setShowEditModal(true);
  }, []);

  return (
    <div className="space-y-4">
      {/* Header + Create button */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search codes..."
            className="w-full pl-8 pr-8 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
              <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>

        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as InfluencerStatus | ''); setPage(1); }}
          className="px-3 py-2 text-xs bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
        >
          <option value="">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="TERMINATED">Terminated</option>
        </select>

        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-primary/20 text-primary border border-primary/30 rounded-lg hover:bg-primary/30 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Create Code
        </button>
      </div>

      {/* Code list */}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No influencer codes found</div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <InfluencerCodeRow
              key={item.id}
              item={item}
              expanded={expandedId === item.id}
              detail={codeDetail?.id === item.id ? codeDetail : null}
              onToggle={() => handleExpand(item.id)}
              onEdit={() => openEditModal(item, codeDetail?.id === item.id ? codeDetail?.data ?? null : null)}
              onDeactivate={() => setDeactivateTarget({ id: item.id, code: item.code })}
            />
          ))}
        </div>
      )}

      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />

      {/* Create Modal */}
      <FormModal
        open={showCreateModal}
        title="Create Influencer Code"
        loading={creating}
        submitLabel={creating ? 'Creating...' : 'Create'}
        onSubmit={handleCreate}
        onCancel={() => setShowCreateModal(false)}
      >
        <div>
          <label className="block text-[10px] text-muted-foreground mb-1">Code *</label>
          <input
            type="text"
            value={createForm.code}
            onChange={(e) => setCreateForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            placeholder="e.g. INFLUENCER2024"
            className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
        </div>
        <div>
          <label className="block text-[10px] text-muted-foreground mb-1">User *</label>
          <SingleUserPicker
            selectedUserId={createForm.userId}
            selectedUserLabel={createForm.userLabel}
            onSelect={(id, label) => setCreateForm((f) => ({ ...f, userId: id, userLabel: label }))}
            onClear={() => setCreateForm((f) => ({ ...f, userId: null, userLabel: '' }))}
          />
        </div>
        <div>
          <label className="block text-[10px] text-muted-foreground mb-1">Payout Method</label>
          <select
            value={createForm.payoutMethod}
            onChange={(e) => setCreateForm((f) => ({ ...f, payoutMethod: e.target.value as PayoutMethod | '' }))}
            className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          >
            <option value="">Not set</option>
            <option value="BANK_TRANSFER">Bank Transfer</option>
            <option value="PAYPAL">PayPal</option>
            <option value="CHECK">Check</option>
            <option value="CREDITS">Credits</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-muted-foreground mb-1">Min Payout (cents)</label>
          <input
            type="number"
            value={createForm.minPayoutCents}
            onChange={(e) => setCreateForm((f) => ({ ...f, minPayoutCents: e.target.value }))}
            placeholder="e.g. 5000"
            className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
        </div>
        <div>
          <label className="block text-[10px] text-muted-foreground mb-1">Notes</label>
          <textarea
            value={createForm.notes}
            onChange={(e) => setCreateForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Optional notes..."
            rows={2}
            className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
          />
        </div>
      </FormModal>

      {/* Edit Modal */}
      <FormModal
        open={showEditModal}
        title="Edit Influencer Code"
        loading={editing}
        submitLabel={editing ? 'Saving...' : 'Save Changes'}
        onSubmit={handleEdit}
        onCancel={() => setShowEditModal(false)}
      >
        <div>
          <label className="block text-[10px] text-muted-foreground mb-1">Status</label>
          <select
            value={editForm.status}
            onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value as InfluencerStatus }))}
            className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          >
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="SUSPENDED">Suspended</option>
            <option value="TERMINATED">Terminated</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-muted-foreground mb-1">Payout Method</label>
          <select
            value={editForm.payoutMethod}
            onChange={(e) => setEditForm((f) => ({ ...f, payoutMethod: e.target.value as PayoutMethod | '' }))}
            className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          >
            <option value="">Not set</option>
            <option value="BANK_TRANSFER">Bank Transfer</option>
            <option value="PAYPAL">PayPal</option>
            <option value="CHECK">Check</option>
            <option value="CREDITS">Credits</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-muted-foreground mb-1">Min Payout (cents)</label>
          <input
            type="number"
            value={editForm.minPayoutCents}
            onChange={(e) => setEditForm((f) => ({ ...f, minPayoutCents: e.target.value }))}
            placeholder="e.g. 5000"
            className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
        </div>
        <div>
          <label className="block text-[10px] text-muted-foreground mb-1">Notes</label>
          <textarea
            value={editForm.notes}
            onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Optional notes..."
            rows={2}
            className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
          />
        </div>
      </FormModal>

      {/* Deactivate Confirm */}
      <ConfirmModal
        open={!!deactivateTarget}
        title="Deactivate Influencer Code"
        confirmLabel={deactivating ? 'Deactivating...' : 'Deactivate'}
        confirmVariant="danger"
        loading={deactivating}
        onConfirm={handleDeactivate}
        onCancel={() => setDeactivateTarget(null)}
      >
        <p>Are you sure you want to deactivate code <span className="text-foreground font-medium font-mono">{deactivateTarget?.code}</span>?</p>
        <p>This action cannot be undone.</p>
      </ConfirmModal>
    </div>
  );
};

const InfluencerCodeRow: React.FC<{
  item: InfluencerCodeItem;
  expanded: boolean;
  detail: { id: number; data: InfluencerCodeDetail | null; loading: boolean } | null;
  onToggle: () => void;
  onEdit: () => void;
  onDeactivate: () => void;
}> = ({ item, expanded, detail, onToggle, onEdit, onDeactivate }) => (
  <div className={cn('card p-3 cursor-pointer transition-all', expanded && 'ring-1 ring-primary/30')} onClick={onToggle}>
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex-shrink-0">
          {expanded ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </div>
        <span className="text-xs font-mono text-foreground">{item.code}</span>
        <span className="text-xs text-muted-foreground truncate max-w-[150px]">{item.userName || item.userEmail || `User #${item.userId}`}</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant={influencerStatusBadge(item.status)} size="sm">{item.status}</Badge>
        <span className="text-[10px] text-muted-foreground">{formatNumber(item.totalReferrals)} referrals</span>
        {item.totalEarningsCents > 0 && <Badge variant="success" size="sm">{formatCentsDecimal(item.totalEarningsCents)}</Badge>}
        {item.pendingPayoutCents > 0 && <span className="text-[10px] text-yellow-400">Pending: {formatCentsDecimal(item.pendingPayoutCents)}</span>}
      </div>
    </div>

    {expanded && (
      <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
        {detail?.loading ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 text-primary animate-spin" /></div>
        ) : detail?.data ? (
          <>
            <DetailSection title="Stats">
              <DetailRow label="Total Referrals" value={formatNumber(detail.data.stats.totalReferrals)} />
              <DetailRow label="Completed" value={formatNumber(detail.data.stats.completedReferrals)} />
              <DetailRow label="Conversion Rate" value={formatPercent(detail.data.stats.conversionRate)} />
              <DetailRow label="Total Earnings" value={formatCentsDecimal(detail.data.stats.totalEarningsCents)} />
              <DetailRow label="Pending Payout" value={formatCentsDecimal(detail.data.stats.pendingPayoutCents)} />
            </DetailSection>

            <DetailSection title="Configuration">
              <DetailRow label="Payout Method" value={detail.data.payoutMethod || 'Not set'} />
              <DetailRow label="Min Payout" value={detail.data.minPayoutCents != null ? formatCentsDecimal(detail.data.minPayoutCents) : 'Not set'} />
              <DetailRow label="Notes" value={detail.data.notes || '—'} />
              <DetailRow label="Created" value={formatDateTime(detail.data.createdAt)} />
              <DetailRow label="Updated" value={formatDateTime(detail.data.updatedAt)} />
            </DetailSection>

            {detail.data.referrals.length > 0 && (
              <div className="pt-2 mt-2">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Referrals ({detail.data.referrals.length})</p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {detail.data.referrals.map((ref) => (
                    <div key={ref.id} className="flex items-center gap-2 p-2 bg-muted/10 rounded-lg text-[10px]">
                      <span className="text-foreground truncate flex-1">{ref.refereeName || `User #${ref.refereeUserId}`}</span>
                      <Badge variant={referralStatusBadge(ref.status)} size="sm">{ref.status}</Badge>
                      {ref.rewardCents != null && ref.rewardCents > 0 && <span className="text-emerald-400">{formatCentsDecimal(ref.rewardCents)}</span>}
                      <span className="text-muted-foreground">{formatRelativeTime(ref.createdAt)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail.data.recentPayouts.length > 0 && (
              <div className="pt-2 mt-2">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Recent Payouts</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {detail.data.recentPayouts.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 p-2 bg-muted/10 rounded-lg text-[10px]">
                      <span className="text-foreground">{formatCentsDecimal(p.amountCents)}</span>
                      <Badge variant="muted" size="sm">{p.method}</Badge>
                      <Badge variant={p.status === 'PAID' ? 'success' : p.status === 'FAILED' ? 'error' : 'warning'} size="sm">{p.status}</Badge>
                      <span className="text-muted-foreground ml-auto">{p.paidAt ? formatDateTime(p.paidAt) : formatDateTime(p.createdAt)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-2 pt-2 border-t border-border">
              <button
                onClick={onEdit}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium bg-primary/15 text-primary border border-primary/30 rounded-lg hover:bg-primary/25 transition-colors"
              >
                <Edit3 className="h-3 w-3" />
                Edit
              </button>
              <button
                onClick={onDeactivate}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium bg-red-500/15 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/25 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
                Deactivate
              </button>
            </div>
          </>
        ) : (
          <p className="text-xs text-muted-foreground text-center py-4">Could not load code detail.</p>
        )}
      </div>
    )}
  </div>
);

// ============================================================================
// Tab: Promo Codes
// ============================================================================

const PROMO_TYPE_LABELS: Record<string, string> = {
  SINGLE_USE: 'Single use',
  MULTI_USE: 'Multi use',
  BULK: 'Bulk',
};

interface PromoCreateForm {
  code: string;
  audience: 'everyone' | 'user';
  /** The named audience. One entry sends targetUserId, several send targetUserIds. */
  targetUsers: PickedUser[];
  /**
   * Issue the credit on create instead of making people type the code. Valid
   * for both audiences — it does not require a target user.
   */
  autoApply: boolean;
  amountDollars: string;
  type: 'SINGLE_USE' | 'MULTI_USE';
  maxRedemptions: string;
  newUsersOnly: boolean;
  maxUsagePercentage: string;
  endsAt: string;
  expirationDays: string;
  campaignName: string;
  campaignSource: string;
}

const EMPTY_PROMO_FORM: PromoCreateForm = {
  code: '',
  audience: 'everyone',
  targetUsers: [],
  autoApply: false,
  amountDollars: '',
  type: 'SINGLE_USE',
  maxRedemptions: '',
  newUsersOnly: false,
  maxUsagePercentage: '',
  endsAt: '',
  expirationDays: '90',
  campaignName: '',
  campaignSource: '',
};

/**
 * What the create actually did. An auto-apply blast to everyone runs in the
 * background, so there is no count to report yet — say so rather than claiming
 * a number that is still climbing.
 */
const createSuccessMessage = (created: CreatePromoCodeResponse): string => {
  const result = created.autoApplyResult;
  if (!result) return `Promo code ${created.code} created`;
  if (result.queued) return `${created.code} created — applying to all users in the background`;

  const applied = `${created.code} applied to ${formatNumber(result.redeemedCount)} user${result.redeemedCount === 1 ? '' : 's'}`;
  if (result.capReached) return `${applied} (stopped at the redemption cap)`;
  if (result.skippedCount > 0) return `${applied} · ${formatNumber(result.skippedCount)} skipped`;
  return applied;
};

const promoErrorMessage = (err: unknown, fallback: string): string => {
  const axiosMessage = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
  if (Array.isArray(axiosMessage)) return axiosMessage.join(', ');
  if (typeof axiosMessage === 'string') return axiosMessage;
  return err instanceof Error ? err.message : fallback;
};

/**
 * Who this code is for, in the row subtitle. Auto-applied codes are phrased as
 * a credit already given, not as something anyone still has to redeem.
 */
const promoAudienceLabel = (item: PromoCodeItem): string => {
  const who = item.targetUser
    ? `${item.targetUser.name || item.targetUser.email || 'user'} #${item.targetUser.id}`
    : null;

  if (item.autoApply) {
    return who ? `Credited to ${who}` : 'Credited to every user, including new signups';
  }
  return who ? `Only ${who} can redeem` : 'Anyone can redeem';
};

const PromoCodeRow: React.FC<{
  item: PromoCodeItem;
  expanded: boolean;
  redemptions: { loading: boolean; data: PromoCodeRedemptionsResponse | null } | null;
  onToggle: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
}> = ({ item, expanded, redemptions, onToggle, onDeactivate, onReactivate }) => {
  const redemptionCap = item.targetUser ? 1 : item.maxRedemptions;
  return (
    <div className="card overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-3 text-left hover:bg-primary/5 transition-colors">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-semibold text-foreground">{item.code}</span>
            <Badge variant={item.isActive ? 'success' : 'muted'}>{item.isActive ? 'Active' : 'Inactive'}</Badge>
            <Badge variant="info">{PROMO_TYPE_LABELS[item.type] || item.type}</Badge>
            {item.autoApply && <Badge variant="success">Auto applied</Badge>}
            {item.newUsersOnly && <Badge variant="warning">New users only</Badge>}
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 truncate">
            {promoAudienceLabel(item)}
            {item.campaignName ? ` · ${item.campaignName}` : ''}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-bold text-foreground">{formatCentsDecimal(item.creditAmountCents)}</p>
          <p className="text-[10px] text-muted-foreground">
            {formatNumber(item.currentRedemptions)}/{redemptionCap != null ? formatNumber(redemptionCap) : '∞'} redeemed
          </p>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-border p-3 space-y-3">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
            <div>
              <p className="text-[10px] text-muted-foreground">Booking cap</p>
              <p className="text-foreground">{item.maxUsagePercentage ?? 100}% per booking</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground">Credit lifetime</p>
              <p className="text-foreground">{item.expirationDays} days after redemption</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground">Code valid until</p>
              <p className="text-foreground">{item.endsAt ? formatDateTime(item.endsAt) : 'No end date'}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground">Created</p>
              <p className="text-foreground">{formatDateTime(item.createdAt)}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {item.isActive ? (
              <button
                onClick={onDeactivate}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
                Deactivate
              </button>
            ) : (
              <button
                onClick={onReactivate}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary/20 text-primary border border-primary/30 rounded-lg hover:bg-primary/30 transition-colors"
              >
                <Plus className="h-3 w-3" />
                Reactivate
              </button>
            )}
          </div>

          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1.5">Redemptions</p>
            {!redemptions || redemptions.loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 text-primary animate-spin" />
              </div>
            ) : !redemptions.data || redemptions.data.redemptions.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">No redemptions yet.</p>
            ) : (
              <div className="divide-y divide-border border border-border rounded-lg">
                {redemptions.data.redemptions.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground truncate">
                        {r.user?.name || r.user?.email || 'User'} <span className="text-muted-foreground">#{r.userId}</span>
                      </p>
                      <p className="text-[10px] text-muted-foreground">{formatRelativeTime(r.redeemedAt)}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-foreground">{formatCentsDecimal(r.credit?.amountCents ?? 0)}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {r.credit ? `${formatCentsDecimal(r.credit.remainingCents)} left · ${r.credit.status}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
                {redemptions.data.total > redemptions.data.redemptions.length && (
                  <p className="text-[10px] text-muted-foreground px-3 py-1.5">
                    Showing {redemptions.data.redemptions.length} of {redemptions.data.total}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PromoCodesTab: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'inactive'>('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [redemptions, setRedemptions] = useState<{ id: number; loading: boolean; data: PromoCodeRedemptionsResponse | null } | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<PromoCreateForm>(EMPTY_PROMO_FORM);
  const [creating, setCreating] = useState(false);

  const [toggleTarget, setToggleTarget] = useState<{ item: PromoCodeItem; action: 'deactivate' | 'reactivate' } | null>(null);
  const [toggling, setToggling] = useState(false);

  const params = useMemo(() => ({
    isActive: statusFilter === '' ? undefined : statusFilter === 'active',
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [statusFilter, page]);

  const { items, total, totalPages, loading, page: currentPage, refetch } = usePromoCodes(params);

  const handleExpand = useCallback(async (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
      setRedemptions(null);
      return;
    }
    setExpandedId(id);
    setRedemptions({ id, loading: true, data: null });
    try {
      const data = await referralsService.getPromoCodeRedemptions(id, { limit: 50 });
      setRedemptions({ id, loading: false, data });
    } catch {
      setRedemptions({ id, loading: false, data: null });
      showToast('error', 'Failed to load redemptions');
    }
  }, [expandedId]);

  const handleCreate = useCallback(async () => {
    const amountCents = Math.round(parseFloat(createForm.amountDollars) * 100);
    if (!createForm.code.trim() || !Number.isFinite(amountCents) || amountCents <= 0) {
      showToast('error', 'Code and a positive credit amount are required');
      return;
    }
    // Auto apply deliberately does NOT require a target — with no target it
    // means every user. Only the "specific users" audience needs a pick.
    const targets = createForm.audience === 'user' ? createForm.targetUsers : [];
    if (createForm.audience === 'user' && targets.length === 0) {
      showToast('error', 'Pick at least one user, or switch audience to Everyone');
      return;
    }

    // SINGLE_USE is one redemption for the WHOLE code, so it cannot express
    // "credit each of these people". One named user is still fine.
    let type = createForm.type;
    if (createForm.autoApply && type === 'SINGLE_USE' && targets.length !== 1) {
      type = 'MULTI_USE';
      showToast(
        'info',
        'Auto apply to more than one person needs Multi use — switched from Single use',
      );
    }

    setCreating(true);
    try {
      const body: CreatePromoCodeRequest = {
        code: createForm.code.trim(),
        type,
        creditAmountCents: amountCents,
        autoApply: createForm.autoApply,
      };
      if (targets.length === 1) body.targetUserId = targets[0].id;
      else if (targets.length > 1) body.targetUserIds = targets.map((u) => u.id);
      if (createForm.maxRedemptions) body.maxRedemptions = parseInt(createForm.maxRedemptions, 10);
      if (createForm.newUsersOnly) body.newUsersOnly = true;
      if (createForm.maxUsagePercentage) body.maxUsagePercentage = parseInt(createForm.maxUsagePercentage, 10);
      if (createForm.endsAt) body.endsAt = new Date(createForm.endsAt).toISOString();
      if (createForm.expirationDays) body.expirationDays = parseInt(createForm.expirationDays, 10);
      if (createForm.campaignName) body.campaignName = createForm.campaignName;
      if (createForm.campaignSource) body.campaignSource = createForm.campaignSource;
      const created = await referralsService.createPromoCode(body);
      showToast('success', createSuccessMessage(created));
      setShowCreateModal(false);
      setCreateForm(EMPTY_PROMO_FORM);
      refetch();
    } catch (err) {
      showToast('error', promoErrorMessage(err, 'Failed to create promo code'));
    } finally {
      setCreating(false);
    }
  }, [createForm, refetch]);

  const handleToggle = useCallback(async () => {
    if (!toggleTarget) return;
    setToggling(true);
    try {
      if (toggleTarget.action === 'deactivate') {
        await referralsService.deactivatePromoCode(toggleTarget.item.id);
        showToast('success', `Code "${toggleTarget.item.code}" deactivated`);
      } else {
        await referralsService.reactivatePromoCode(toggleTarget.item.id);
        showToast('success', `Code "${toggleTarget.item.code}" reactivated`);
      }
      setToggleTarget(null);
      refetch();
    } catch (err) {
      showToast('error', promoErrorMessage(err, 'Failed to update promo code'));
    } finally {
      setToggling(false);
    }
  }, [toggleTarget, refetch]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as '' | 'active' | 'inactive'); setPage(1); }}
          className="px-3 py-2 text-xs bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
        >
          <option value="">All Codes</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        <div className="flex-1" />

        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-primary/20 text-primary border border-primary/30 rounded-lg hover:bg-primary/30 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Create Code
        </button>
      </div>

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No promo codes found</div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <PromoCodeRow
              key={item.id}
              item={item}
              expanded={expandedId === item.id}
              redemptions={redemptions?.id === item.id ? redemptions : null}
              onToggle={() => handleExpand(item.id)}
              onDeactivate={() => setToggleTarget({ item, action: 'deactivate' })}
              onReactivate={() => setToggleTarget({ item, action: 'reactivate' })}
            />
          ))}
        </div>
      )}

      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />

      {/* Create Modal */}
      <FormModal
        open={showCreateModal}
        title="Create Promo Code"
        loading={creating}
        submitLabel={creating ? 'Creating...' : 'Create'}
        onSubmit={handleCreate}
        onCancel={() => setShowCreateModal(false)}
      >
        <div>
          <label className="block text-[10px] text-muted-foreground mb-1">Code *</label>
          <input
            type="text"
            value={createForm.code}
            onChange={(e) => setCreateForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            placeholder="e.g. WELCOME25"
            className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
        </div>

        <div>
          <label className="block text-[10px] text-muted-foreground mb-1">Credit amount (USD) *</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={createForm.amountDollars}
            onChange={(e) => setCreateForm((f) => ({ ...f, amountDollars: e.target.value }))}
            placeholder="e.g. 25.00"
            className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
        </div>

        <div>
          <label className="block text-[10px] text-muted-foreground mb-1">Audience *</label>
          <div className="flex items-center gap-2 mb-2">
            <button
              onClick={() => setCreateForm((f) => ({ ...f, audience: 'everyone', targetUsers: [] }))}
              className={cn(
                'px-3 py-1.5 text-xs rounded-lg border transition-colors',
                createForm.audience === 'everyone'
                  ? 'bg-primary/20 text-primary border-primary/30'
                  : 'bg-card text-muted-foreground border-border hover:text-foreground'
              )}
            >
              Everyone
            </button>
            <button
              onClick={() => setCreateForm((f) => ({ ...f, audience: 'user' }))}
              className={cn(
                'px-3 py-1.5 text-xs rounded-lg border transition-colors',
                createForm.audience === 'user'
                  ? 'bg-primary/20 text-primary border-primary/30'
                  : 'bg-card text-muted-foreground border-border hover:text-foreground'
              )}
            >
              Specific users
            </button>
          </div>
          {createForm.audience === 'user' && (
            <UserPicker
              multiple
              selected={createForm.targetUsers}
              onAdd={(user) =>
                setCreateForm((f) =>
                  f.targetUsers.some((u) => u.id === user.id)
                    ? f
                    : { ...f, targetUsers: [...f.targetUsers, user] }
                )
              }
              onRemove={(userId) =>
                setCreateForm((f) => ({
                  ...f,
                  targetUsers: f.targetUsers.filter((u) => u.id !== userId),
                }))
              }
            />
          )}
        </div>

        {/* Auto apply — valid for both audiences, and never needs a target. */}
        <div className="p-3 bg-card border border-border rounded-lg">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={createForm.autoApply}
              onChange={(e) => setCreateForm((f) => ({ ...f, autoApply: e.target.checked }))}
              className="mt-0.5 rounded border-border"
            />
            <span>
              <span className="text-xs font-medium text-foreground">Auto apply</span>
              <span className="block text-[10px] text-muted-foreground mt-0.5">
                {createForm.audience === 'everyone'
                  ? 'Every current user, and new signups while this code is active, get this credit. They do not enter a code.'
                  : 'These users get the credit immediately. They do not enter a code.'}
              </span>
            </span>
          </label>
          {createForm.autoApply && createForm.type === 'SINGLE_USE' && createForm.targetUsers.length !== 1 && (
            <p className="text-[10px] text-amber-400 mt-2 pl-6">
              Single use is one redemption for the whole code — this will be created as Multi use.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] text-muted-foreground mb-1">Type</label>
            <select
              value={createForm.type}
              onChange={(e) => setCreateForm((f) => ({ ...f, type: e.target.value as 'SINGLE_USE' | 'MULTI_USE' }))}
              className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            >
              <option value="SINGLE_USE">Single use (one redemption total)</option>
              <option value="MULTI_USE">Multi use (once per user)</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-muted-foreground mb-1">Max redemptions</label>
            <input
              type="number"
              min="1"
              value={createForm.maxRedemptions}
              onChange={(e) => setCreateForm((f) => ({ ...f, maxRedemptions: e.target.value }))}
              placeholder="Unlimited"
              className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] text-muted-foreground mb-1">Max % of a booking</label>
            <input
              type="number"
              min="1"
              max="100"
              value={createForm.maxUsagePercentage}
              onChange={(e) => setCreateForm((f) => ({ ...f, maxUsagePercentage: e.target.value }))}
              placeholder="Default: 100 (full value)"
              className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
          <div>
            <label className="block text-[10px] text-muted-foreground mb-1">Credit expires after (days)</label>
            <input
              type="number"
              min="1"
              value={createForm.expirationDays}
              onChange={(e) => setCreateForm((f) => ({ ...f, expirationDays: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
        </div>

        <div>
          <label className="block text-[10px] text-muted-foreground mb-1">Code valid until</label>
          <input
            type="date"
            value={createForm.endsAt}
            onChange={(e) => setCreateForm((f) => ({ ...f, endsAt: e.target.value }))}
            className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={createForm.newUsersOnly}
            onChange={(e) => setCreateForm((f) => ({ ...f, newUsersOnly: e.target.checked }))}
            className="rounded border-border"
          />
          <span className="text-xs text-foreground">New users only (no completed bookings yet)</span>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] text-muted-foreground mb-1">Campaign name</label>
            <input
              type="text"
              value={createForm.campaignName}
              onChange={(e) => setCreateForm((f) => ({ ...f, campaignName: e.target.value }))}
              placeholder="Optional"
              className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
          <div>
            <label className="block text-[10px] text-muted-foreground mb-1">Campaign source</label>
            <input
              type="text"
              value={createForm.campaignSource}
              onChange={(e) => setCreateForm((f) => ({ ...f, campaignSource: e.target.value }))}
              placeholder="Optional"
              className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
        </div>
      </FormModal>

      {/* Deactivate / Reactivate confirm */}
      <ConfirmModal
        open={toggleTarget !== null}
        title={toggleTarget?.action === 'deactivate' ? 'Deactivate Promo Code' : 'Reactivate Promo Code'}
        confirmLabel={toggling ? 'Working...' : toggleTarget?.action === 'deactivate' ? 'Deactivate' : 'Reactivate'}
        confirmVariant={toggleTarget?.action === 'deactivate' ? 'danger' : 'primary'}
        loading={toggling}
        onConfirm={handleToggle}
        onCancel={() => setToggleTarget(null)}
      >
        {toggleTarget?.action === 'deactivate' ? (
          <p>
            "{toggleTarget?.item.code}" will stop being redeemable immediately. Credits already
            issued from it are not affected.
          </p>
        ) : (
          <p>"{toggleTarget?.item.code}" will become redeemable again.</p>
        )}
      </ConfirmModal>
    </div>
  );
};

// ============================================================================
// Tab 3: Referees
// ============================================================================

const RefereesTab: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReferralStatus | ''>('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const params = useMemo(() => ({
    referralCode: debouncedSearch || undefined,
    type: undefined as ActivityType | undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [debouncedSearch, page]);

  const { items: allItems, total, totalPages, loading, page: currentPage } = useReferralActivity(params);

  // Client-side status filter (activity endpoint doesn't support status param)
  const items = useMemo(() => {
    if (!statusFilter) return allItems;
    return allItems.filter((item) => item.status === statusFilter);
  }, [allItems, statusFilter]);

  const filteredTotal = statusFilter ? items.length : total;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by referral code..."
            className="w-full pl-8 pr-8 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
              <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>

        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as ReferralStatus | ''); setPage(1); }}
          className="px-3 py-2 text-xs bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
        >
          <option value="">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="QUALIFIED">Qualified</option>
          <option value="CREDITS_ISSUED">Credits Issued</option>
          <option value="INVALID">Invalid</option>
        </select>
      </div>

      {/* Referees list */}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No referees found</div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <RefereeRow
              key={item.id}
              item={item}
              expanded={expandedId === item.id}
              onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
            />
          ))}
        </div>
      )}

      <Pagination
        page={currentPage}
        totalPages={statusFilter ? 1 : totalPages}
        total={filteredTotal}
        onPageChange={setPage}
        loading={loading}
      />
    </div>
  );
};

const RefereeRow: React.FC<{
  item: ActivityItem;
  expanded: boolean;
  onToggle: () => void;
}> = ({ item, expanded, onToggle }) => (
  <div className={cn('card p-3 cursor-pointer transition-all', expanded && 'ring-1 ring-primary/30')} onClick={onToggle}>
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex-shrink-0">
          {expanded ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </div>
        {/* Referee (the person who signed up) */}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-foreground truncate">
            {item.refereeName || item.refereeEmail || `User #${item.refereeUserId}`}
          </p>
          <p className="text-[10px] text-muted-foreground">
            referred by <span className="text-foreground">{item.referrerName || `User #${item.referrerUserId}`}</span>
          </p>
        </div>
        <span className="text-[10px] font-mono text-muted-foreground">{item.referralCode}</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant={referralStatusBadge(item.status)} size="sm">{item.status}</Badge>
        {item.referrerRewardCents != null && item.referrerRewardCents > 0 && (
          <span className="text-[10px] text-emerald-400">{formatCentsDecimal(item.referrerRewardCents)} referrer</span>
        )}
        {item.refereeRewardCents != null && item.refereeRewardCents > 0 && (
          <span className="text-[10px] text-blue-400">{formatCentsDecimal(item.refereeRewardCents)} referee</span>
        )}
        <span className="text-[10px] text-muted-foreground">{formatRelativeTime(item.timestamp || item.createdAt)}</span>
      </div>
    </div>

    {expanded && (
      <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
        <DetailSection title="Referee">
          <DetailRow label="Name" value={item.refereeName || '—'} />
          <DetailRow label="Email" value={item.refereeEmail || '—'} />
          <DetailRow label="User ID" value={String(item.refereeUserId)} />
        </DetailSection>
        <DetailSection title="Referrer">
          <DetailRow label="Name" value={item.referrerName || '—'} />
          <DetailRow label="Email" value={item.referrerEmail || '—'} />
          <DetailRow label="User ID" value={String(item.referrerUserId)} />
          <DetailRow label="Referral Code" value={item.referralCode} />
        </DetailSection>
        <DetailSection title="Referral Status">
          <DetailRow label="Status" value={<Badge variant={referralStatusBadge(item.status)} size="sm">{item.status}</Badge>} />
          <DetailRow label="Program" value={item.programName || '—'} />
          <DetailRow label="Referrer Reward" value={item.referrerRewardCents != null ? formatCentsDecimal(item.referrerRewardCents) : '—'} />
          <DetailRow label="Referee Reward" value={item.refereeRewardCents != null ? formatCentsDecimal(item.refereeRewardCents) : '—'} />
        </DetailSection>
        <DetailSection title="Timeline">
          <DetailRow label="Signed Up" value={formatDateTime(item.timestamp || item.createdAt)} />
          {item.qualifiedAt && <DetailRow label="Qualified" value={formatDateTime(item.qualifiedAt)} />}
          {item.creditsIssuedAt && <DetailRow label="Credits Issued" value={formatDateTime(item.creditsIssuedAt)} />}
          {item.invalidatedAt && <DetailRow label="Invalidated" value={formatDateTime(item.invalidatedAt)} />}
          {item.invalidationReason && <DetailRow label="Reason" value={<span className="text-red-400">{item.invalidationReason}</span>} />}
        </DetailSection>
      </div>
    )}
  </div>
);

// ============================================================================
// Tab 5: Activity
// ============================================================================

const ActivityTab: React.FC = () => {
  const [typeFilter, setTypeFilter] = useState<ActivityType | ''>('');
  const [codeSearch, setCodeSearch] = useState('');
  const [debouncedCode, setDebouncedCode] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCode(codeSearch);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [codeSearch]);

  const params = useMemo(() => ({
    type: typeFilter || undefined,
    referralCode: debouncedCode || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [typeFilter, debouncedCode, page]);

  const { items, total, totalPages, loading, page: currentPage } = useReferralActivity(params);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value as ActivityType | ''); setPage(1); }}
          className="px-3 py-2 text-xs bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
        >
          <option value="">All Types</option>
          <option value="SIGNUP">Signup</option>
          <option value="QUALIFIED">Qualified</option>
          <option value="CREDITS_ISSUED">Credits Issued</option>
          <option value="CREDIT_USED">Credit Used</option>
          <option value="INVALIDATED">Invalidated</option>
        </select>

        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            value={codeSearch}
            onChange={(e) => setCodeSearch(e.target.value)}
            placeholder="Filter by referral code..."
            className="w-full pl-8 pr-8 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
          {codeSearch && (
            <button onClick={() => setCodeSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
              <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Activity list */}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No activity found</div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <ActivityRow
              key={item.id}
              item={item}
              expanded={expandedId === item.id}
              onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
            />
          ))}
        </div>
      )}

      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

const ActivityRow: React.FC<{
  item: ActivityItem;
  expanded: boolean;
  onToggle: () => void;
}> = ({ item, expanded, onToggle }) => (
  <div className={cn('card p-3 cursor-pointer transition-all', expanded && 'ring-1 ring-primary/30')} onClick={onToggle}>
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex-shrink-0">
          {expanded ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </div>
        <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">
          {formatRelativeTime(item.timestamp || item.createdAt)}
        </div>
        <Badge variant={activityTypeBadge(item.type)} size="sm">{item.type}</Badge>
        <span className="text-xs text-foreground truncate">
          {item.referrerName || `User #${item.referrerUserId}`}
        </span>
        <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        <span className="text-xs text-foreground truncate">
          {item.refereeName || `User #${item.refereeUserId}`}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-mono text-muted-foreground">{item.referralCode}</span>
        <Badge variant={referralStatusBadge(item.status)} size="sm">{item.status}</Badge>
        {item.referrerRewardCents != null && item.referrerRewardCents > 0 && (
          <span className="text-[10px] text-emerald-400">{formatCentsDecimal(item.referrerRewardCents)}</span>
        )}
        {item.refereeRewardCents != null && item.refereeRewardCents > 0 && (
          <span className="text-[10px] text-blue-400">+{formatCentsDecimal(item.refereeRewardCents)}</span>
        )}
      </div>
    </div>

    {expanded && (
      <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
        <DetailSection title="Details">
          <DetailRow label="Type" value={item.type} />
          <DetailRow label="Referral Code" value={item.referralCode} />
          <DetailRow label="Status" value={<Badge variant={referralStatusBadge(item.status)} size="sm">{item.status}</Badge>} />
          <DetailRow label="Program" value={item.programName || '—'} />
        </DetailSection>
        <DetailSection title="People">
          <DetailRow label="Referrer" value={`${item.referrerName || 'Unknown'} (#${item.referrerUserId})`} />
          <DetailRow label="Referee" value={`${item.refereeName || 'Unknown'} (#${item.refereeUserId})`} />
        </DetailSection>
        <DetailSection title="Rewards">
          <DetailRow label="Referrer Reward" value={item.referrerRewardCents != null ? formatCentsDecimal(item.referrerRewardCents) : '—'} />
          <DetailRow label="Referee Reward" value={item.refereeRewardCents != null ? formatCentsDecimal(item.refereeRewardCents) : '—'} />
        </DetailSection>
        <DetailSection title="Timeline">
          <DetailRow label="Created" value={formatDateTime(item.createdAt)} />
          {item.qualifiedAt && <DetailRow label="Qualified" value={formatDateTime(item.qualifiedAt)} />}
          {item.creditsIssuedAt && <DetailRow label="Credits Issued" value={formatDateTime(item.creditsIssuedAt)} />}
          {item.invalidatedAt && <DetailRow label="Invalidated" value={formatDateTime(item.invalidatedAt)} />}
          {item.invalidationReason && <DetailRow label="Reason" value={<span className="text-red-400">{item.invalidationReason}</span>} />}
        </DetailSection>
      </div>
    )}
  </div>
);

// ============================================================================
// Main Page
// ============================================================================

const Referrals: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  const TABS: { key: TabKey; label: string; icon: React.ElementType }[] = [
    { key: 'overview', label: 'Overview', icon: Gift },
    { key: 'users', label: 'Users', icon: Users },
    { key: 'referees', label: 'Referees', icon: UserCheck },
    { key: 'influencer-codes', label: 'Influencer Codes', icon: Code2 },
    { key: 'promo-codes', label: 'Promo Codes', icon: Tag },
    { key: 'activity', label: 'Activity', icon: Activity },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Tab bar */}
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center gap-1 px-4 py-2 overflow-x-auto scrollbar-thin">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all whitespace-nowrap',
                  isActive
                    ? 'bg-primary/15 text-primary font-medium border border-primary/30'
                    : 'text-muted-foreground hover:bg-primary/10 hover:text-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4 lg:p-6">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'referees' && <RefereesTab />}
        {activeTab === 'influencer-codes' && <InfluencerCodesTab />}
        {activeTab === 'promo-codes' && <PromoCodesTab />}
        {activeTab === 'activity' && <ActivityTab />}
      </div>
    </div>
  );
};

export default Referrals;
